/*
 * The Account page's data and arithmetic, ported from Android
 * (estimatebuilder/account/HoursWeeks.kt).
 */

// ---- Hours worked (HoursWeeks.kt) ---------------------------------------------------------

export interface HoursWorkedWeekDto {startDate?: string | null; days: {day: string; hours: number}[]}

export interface MyProfileDto {
  id: number;
  email: string;
  name: string;
  phoneNumber?: string | null;
  streetAddress?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  levelName?: string | null;
  hoursWorked?: HoursWorkedWeekDto[] | null;
}

export const WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

/** A week as Monday's date (local, midnight) and minutes for Monday … Sunday. */
export interface HoursWeek {start: Date; minutes: number[]}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const dayKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
export const parseDay = (s?: string | null): Date | undefined => {
  const m = s?.slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : undefined;
};
export const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
export const mondayOf = (d: Date) => addDays(d, -((d.getDay() + 6) % 7));
const weeksApart = (a: Date, b: Date) => Math.round((b.getTime() - a.getTime()) / (7 * 86400000));

export const total = (w: HoursWeek) => w.minutes.reduce((a, b) => a + b, 0);

/** Stored weeks by Monday; two on one Monday are summed by day (weeksFromDto). */
export const weeksFromDto = (weeks: HoursWorkedWeekDto[]): Map<string, HoursWeek> => {
  const out = new Map<string, HoursWeek>();
  weeks.forEach(week => {
    const start = parseDay(week.startDate);
    if (!start) return;
    const monday = mondayOf(start);
    const slot = out.get(dayKey(monday)) ?? {start: monday, minutes: Array(7).fill(0)};
    week.days.forEach(day => {
      const i = WEEKDAYS.indexOf(day.day.toLowerCase());
      if (i >= 0) slot.minutes[i] += Math.round(day.hours * 60);
    });
    out.set(dayKey(monday), slot);
  });
  return out;
};

const emptyWeek = (monday: Date): HoursWeek => ({start: monday, minutes: Array(7).fill(0)});

export const recentWeeks = (stored: Map<string, HoursWeek>, today: Date, count: number): HoursWeek[] => {
  const thisMonday = mondayOf(today);
  return Array.from({length: count}, (_, back) => {
    const monday = addDays(thisMonday, -7 * back);
    return stored.get(dayKey(monday)) ?? emptyWeek(monday);
  });
};

/** A range up to this many weeks lists every week in it, empty ones included. */
export const FULL_RANGE_WEEKS = 8;

export const weeksBetween = (stored: Map<string, HoursWeek>, from: Date, to: Date): HoursWeek[] => {
  const [a, b] = from <= to ? [from, to] : [to, from];
  const first = mondayOf(a);
  const last = mondayOf(b);
  if (weeksApart(first, last) + 1 > FULL_RANGE_WEEKS) {
    return Array.from(stored.values())
      .filter(w => w.start >= first && w.start <= last && total(w) > 0)
      .sort((x, y) => y.start.getTime() - x.start.getTime());
  }
  const out: HoursWeek[] = [];
  for (let monday = last; monday >= first; monday = addDays(monday, -7)) out.push(stored.get(dayKey(monday)) ?? emptyWeek(monday));
  return out;
};

export const isLongRange = (from: Date, to: Date) =>
  weeksApart(mondayOf(from <= to ? from : to), mondayOf(from <= to ? to : from)) + 1 > FULL_RANGE_WEEKS;

/**
 * The stored list with one day changed (withDayMinutes). The week is matched
 * by its Monday; every stored week on that Monday is folded into one (so a
 * sum shown on screen is exactly what gets written), and weeks for other
 * dates are kept as they were, in place.
 */
export const withDayMinutes = (stored: HoursWorkedWeekDto[], week: HoursWeek, dayIndex: number, minutes: number): HoursWorkedWeekDto[] => {
  const updated = week.minutes.map((m, i) => i === dayIndex ? minutes : m);
  const days = WEEKDAYS.map((day, i) => ({day, hours: updated[i] / 60})).filter(x => x.hours > 0);
  const sameMonday = (w: HoursWorkedWeekDto) => {
    const d = parseDay(w.startDate);
    return !!d && dayKey(mondayOf(d)) === dayKey(week.start);
  };
  const firstIndex = stored.findIndex(sameMonday);
  const kept = stored.filter(w => !sameMonday(w));
  if (days.length === 0) return kept;
  const at = firstIndex < 0 ? 0 : stored.slice(0, firstIndex).filter(w => !sameMonday(w)).length;
  kept.splice(at, 0, {startDate: dayKey(week.start), days});
  return kept;
};

/** "10h", "8h 30m", "45m", "0m". */
export const hoursLabel = (minutes: number) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h === 0 ? `${m}m` : m === 0 ? `${h}h` : `${h}h ${m}m`;
};

export const dayLabel = (d: Date) => `${DAYS[d.getDay()]}, ${MONTHS[d.getMonth()]} ${d.getDate()}`;
const monthDay = (d: Date) => `${MONTHS[d.getMonth()]} ${d.getDate()}`;
export const weekLabel = (w: HoursWeek) => `Week of ${monthDay(w.start)} to ${monthDay(addDays(w.start, 6))}`;

// ---- Small formatting ---------------------------------------------------------------------

export const initialsOf = (name: string) =>
  name.trim().split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('') || '?';

/** "Owner, Level 5" from "Level 5 (Owner / Admin)" (AccountScreen.levelLabel). */
export const levelLabel = (name?: string | null) => {
  if (!name?.trim()) return 'No permission level';
  const m = name.match(/^\s*(Level\s+\d+)\s*\((.+)\)\s*$/);
  return m ? `${m[2].split('/')[0].trim()}, ${m[1]}` : name;
};

export const joinAddress = (street?: string | null, city?: string | null, state?: string | null, zip?: string | null) => {
  const stateZip = [state?.trim(), zip?.trim()].filter(Boolean).join(' ');
  return [street?.trim(), city?.trim(), stateZip].filter(Boolean).join(', ');
};
